import React, { useState, useEffect } from 'react';
import { List, Divider, Skeleton, message, Typography, Card, Button, Table, Tag, Modal } from 'antd';
import UserStore from 'stores/UserStore';
import Remove from 'components/EmailActions/Remove/';
import Send from 'components/EmailActions/Send/';
import SetPrimary from 'components/EmailActions/SetPrimary/';
import AddEmail from 'components/EmailActions/AddEmail/';
import UIStore from '@/stores/UIStore';
import { IEmail } from 'types/types';

import { CheckCircleFilled } from '@ant-design/icons';

const { Paragraph, Title } = Typography;

const ManageEmails: React.FC = () => {
    const [data, setData] = useState<IEmail[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const emailsReady = !loading && data.some(item => item.primary);

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        async function getMyEmails() {
            try {
                setLoading(true);
                UIStore.setSelectedKey(['emails']);
                UIStore.setDocumentTitle('Emails');

                await UserStore.getEmails().then(data => {
                    setData(data.results);
                });

                setLoading(false);
            } catch (err: any) {
                setLoading(false);
                message.error(`Failed loading Email list. Error: ${err}`);
            }
        }

        getMyEmails();
    }, []);

    const updateData = async () => {
        try {
            setLoading(true);

            await UserStore.getEmails().then(data => {
                setData(data.results);
                setLoading(false);
            });
        } catch (err: any) {
            setLoading(false);
            message.error(`Failed updating email. Error: ${err}`);
        }
    };

    const handleRemove = async (id: number) => {
        try {
            setLoading(true);
            setData(
                data.filter(item => {
                    return item.id !== id;
                })
            );
            setLoading(false);
        } catch (err: any) {
            setLoading(false);
            message.error(`Failed removing email. Error: ${err}`);
        }
    };

    return (
        <section data-testid="page-customer-settings-emails" data-ready={emailsReady ? 'true' : 'false'}>
            <Modal
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                okText="Remove"
                footer={null}
                closable={true}
            >
                <Title level={4}>Add Email Address</Title>
                <AddEmail updateData={updateData} setModalVisible={setModalVisible} />
            </Modal>

            <Card
                title="Manage Emails"
                bordered
                extra={[
                    <Button
                        key="1"
                        data-testid="customer-settings-add-email-btn"
                        onClick={() => setModalVisible(true)}
                        className="ant-btn-dark mt-3"
                        type="primary"
                        size="small"
                    >
                        Add Email
                    </Button>,
                ]}
            >
                <Paragraph>
                    Your primary email address the login address for your account. Every additional email requires
                    verification to use. Once verified, Orders and Quotes made through that address will populate within
                    this account.
                </Paragraph>
                <Table
                    data-testid="customer-settings-email-list"
                    data-ready={emailsReady ? 'true' : 'false'}
                    loading={loading}
                    dataSource={data}
                    onRow={record =>
                        ({
                            'data-testid': 'customer-settings-email-row',
                            'data-email': record.email,
                            'data-primary': record.primary ? 'true' : 'false',
                            'data-verified': record.verified ? 'true' : 'false',
                        } as React.HTMLAttributes<HTMLTableRowElement>)
                    }
                    pagination={false}
                    columns={[
                        {
                            key: 'email',
                            title: 'Email Address',
                            render: record => (
                                <>
                                    <span className="mr-2">{record.email}</span>
                                    {record.primary ? (
                                        <Tag color="green" icon={<CheckCircleFilled />}>
                                            Primary
                                        </Tag>
                                    ) : null}
                                </>
                            ),
                        },
                        {
                            key: 'verified',
                            title: 'Status',
                            render: record => (
                                <>
                                    <Tag color={record.verified ? 'green' : 'volcano'}>
                                        {record.verified ? 'Verified' : 'Not Verified'}
                                    </Tag>
                                    <Send id={record.id} />
                                </>
                            ),
                        },
                        {
                            key: 'actions',
                            align: 'right',
                            render: record => (
                                <div className="d-flex flex-row justify-content-end" style={{ columnGap: '0.5rem' }}>
                                    {record.verified ? (
                                        <SetPrimary
                                            isPrimary={record.primary}
                                            email={record.email}
                                            updateData={updateData}
                                            id={record.id}
                                        />
                                    ) : null}
                                    {!record.primary ? (
                                        <Remove email={record.email} handleRemove={handleRemove} id={record.id} />
                                    ) : null}
                                </div>
                            ),
                        },
                    ]}
                />
            </Card>
        </section>
    );
};

export default ManageEmails;
